import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/cards';
import { Selector } from '@/components/ui/inputs/selector';
import { IconScout } from '@/components/ui/images/IconScout';
import { Text } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';
import { zIndex } from '@/assets/styles';
import { useMonitoringFilters } from '@/hooks/attendance/monitoring/useMonitoringFilters';
import type { LC_MonitoringFiltersIn } from '@/api/domains/attendance/monitoring';


interface MonitoringFiltersPanelProps {
  // Emits the assembled transport payload whenever the filters change. The consuming page
  // forwards it to the counters/listings once those tickets land. Per Figma the panel has no
  // explicit "Consultar" button, so the payload is emitted live on every change.
  onApply: (payload: LC_MonitoringFiltersIn) => void;
}

export const MonitoringFiltersPanel: React.FC<MonitoringFiltersPanelProps> = ({ onApply }) => {
  const { t: translate } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const {
    filters,
    catalogOptions,
    modalityOptions,
    scheduleRestrictionOptions,
    modalityValue,
    scheduleRestrictionValue,
    handlers,
    buildRequest,
  } = useMonitoringFilters();

  // No apply button in the design: emit the payload whenever the filters change.
  useEffect(() => {
    onApply(buildRequest());
  }, [buildRequest, onApply]);

  const allFeminine = translate('attendance:monitoring.filters.allFeminine');
  const allMasculine = translate('attendance:monitoring.filters.all');

  const heading = (
    <button
      type="button"
      onClick={() => setIsOpen((previous) => !previous)}
      className="flex items-center gap-2 pl-4"
      aria-expanded={isOpen}
      data-testid="monitoring-filters-toggle"
    >
      <Text variant="body-regular" color={colors.primary.dash}>
        {translate('attendance:monitoring.filters.heading')}
      </Text>
      <IconScout name={isOpen ? 'angleUp' : 'angleDown'} size="lg" color={colors.primary.dash} />
    </button>
  );

  // The header stays in the normal flow so toggling never shifts the counters/tables; the
  // options card floats above the content (absolute overlay) when expanded, matching Figma.
  return (
    <div className="relative w-full sm:w-[276px]" data-testid="monitoring-filters-panel">
      {heading}

      {isOpen && (
        <div
          className="absolute left-0 top-full mt-2 w-full sm:w-[276px]"
          style={{ zIndex: zIndex.menuDropdown }}
        >
          <Card elevation={2} className="rounded-2xl">
            <div className="flex flex-col gap-2 px-4 pb-6 pt-4">
              <Selector
                label={translate('attendance:monitoring.filters.company')}
                value={filters.companyIds}
                options={catalogOptions.companyOptions}
                onChange={handlers.onCompanyChange}
                multiple
                searchable
                placeholder={allFeminine}
                data-testid="monitoring-company-selector"
              />
              <Selector
                label={translate('attendance:monitoring.filters.area')}
                value={filters.areaIds}
                options={catalogOptions.areaOptions}
                onChange={handlers.onAreaChange}
                multiple
                searchable
                placeholder={allFeminine}
                data-testid="monitoring-area-selector"
              />
              <Selector
                label={translate('attendance:monitoring.filters.costCenter')}
                value={filters.costCenterIds}
                options={catalogOptions.costCenterOptions}
                onChange={handlers.onCostCenterChange}
                multiple
                searchable
                placeholder={allMasculine}
                data-testid="monitoring-cost-center-selector"
              />
              <Selector
                label={translate('attendance:monitoring.filters.location')}
                value={filters.locationIds}
                options={catalogOptions.locationOptions}
                onChange={handlers.onLocationChange}
                multiple
                searchable
                placeholder={allFeminine}
                data-testid="monitoring-location-selector"
              />
              <Selector
                label={translate('attendance:monitoring.filters.securityGroup')}
                value={filters.groupIds}
                options={catalogOptions.securityGroupOptions}
                onChange={handlers.onSecurityGroupChange}
                multiple
                searchable
                placeholder={allMasculine}
                data-testid="monitoring-security-group-selector"
              />
              <Selector
                label={translate('attendance:monitoring.filters.scheduleRestriction')}
                value={scheduleRestrictionValue}
                options={scheduleRestrictionOptions}
                onChange={handlers.onScheduleRestrictionChange}
                searchable={false}
                data-testid="monitoring-schedule-restriction-selector"
              />
              <Selector
                label={translate('attendance:monitoring.filters.contractModality')}
                value={modalityValue}
                options={modalityOptions}
                onChange={handlers.onModalityChange}
                searchable
                data-testid="monitoring-contract-modality-selector"
              />
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MonitoringFiltersPanel;
